import React from 'react';
import { Box, Typography } from '@mui/material';
import { QuestionListRow } from '../QuestionListRow';
import { RESULT_FILTERS, ResultsFilterTabs, matchesFilter } from './ResultsFilterTabs';

/**
 * The results page as a phone opens it: every question in one scrolling list,
 * narrowed to the ones that went right or wrong, with each row opening that
 * question on its own.
 *
 * A phone has room for one question at a time, so the marked quiz is read from
 * a list rather than scrolled through — and the numbers stay the quiz's own
 * however the list is filtered, so a question keeps the number it was answered
 * under.
 *
 * Props:
 *   id             (string)   - ID prefix for generated element IDs.
 *   results        (array)    - Output of gradeQuiz, one entry per question.
 *   filter         (string)   - Which of RESULT_FILTERS is on screen, already
 *                               resolved by the page that owns it.
 *   isFilterable   (bool)     - Whether the quiz has both right and wrong answers
 *                               to tell apart, and so anything to offer tabs for.
 *   onFilterChange (function) - Called with the filter to switch to.
 *   onSelect       (function) - Called with the index of the question to open.
 */
export const QuizResultsList = ({
  id,
  results,
  filter,
  isFilterable,
  onFilterChange,
  onSelect,
}) => {
  const shown = results
    .map((result, index) => ({ result, index }))
    .filter(({ result }) => matchesFilter(result, filter));

  const { empty } = RESULT_FILTERS.find(({ value }) => value === filter) ?? RESULT_FILTERS[0];

  return (
    <Box className="quizResults" id={`${id}__results`}>
      {isFilterable && <ResultsFilterTabs id={id} filter={filter} onChange={onFilterChange} />}

      {shown.length === 0 ? (
        <Typography className="resultsEmpty" id={`${id}__text_resultsEmpty`}>
          {empty}
        </Typography>
      ) : (
        <Box className="resultsList" component="nav" aria-label="Quiz questions">
          {shown.map(({ result, index }) => (
            <QuestionListRow
              key={index}
              id={`${id}__resultRow_${index + 1}`}
              number={index + 1}
              text={result.question.question}
              result={result}
              isGraded
              onSelect={() => onSelect(index)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
